// src/redux/store/index.js
import { configureStore, EnhancedStore } from '@reduxjs/toolkit';
import { combineReducers } from 'redux';
import { persistStore, persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import treatmentPlanReducer from '../slices/treatmentPlan';
import createSagaMiddleware from 'redux-saga';
import authReducer from '../slices/authSlice';
import rootSaga from '../sagas/rootSaga';
import patientsReducer from '../slices/patientsSlice';
import analyticsReducer from '../slices/analyticsSlice';
import usersReducer from '../slices/usersSlice';
import { createLogger } from 'redux-logger';

// Configure persist for treatment plan
const treatmentPlanPersistConfig = {
  key: 'treatmentPlan',
  storage,
  // Only persist the currentTreatmentPlan field
  whitelist: ['currentTreatmentPlan', 'sentToPatientsIds'],
};

// Configure persist for auth
const authPersistConfig = {
  key: 'auth',
  storage,
  // Persist user and tokens
  whitelist: ['user', 'tokens'],
  version: 2,
  migrate: (state: any) => {
    if (!state) return Promise.resolve(state);
    const isEmpty = (v: any) => v === '' || v === null || v === undefined;
    const access = state.tokens?.access;
    const refresh = state.tokens?.refresh;
    // If tokens are missing/empty, clear user as well to avoid ghost sessions
    if (!state.tokens || isEmpty(access) || isEmpty(refresh)) {
      state.user = null;
      state.tokens = { access: null, refresh: null };
    }
    return Promise.resolve(state);
  },
};

// Main persist config
const persistConfig = {
  key: 'root',
  storage,
  version: 3, // Increment version to force migration for auth persistence
  // Don't persist treatment plan and auth at root level as we're handling them separately
  blacklist: ['treatmentPlan', 'auth'],
  // Add migration to handle new state structure
  migrate: (state: any) => {
    // Handle case where state is undefined (first load) or null
    if (!state) {
      return Promise.resolve(state);
    }

    // Ensure patients state has the new updateMedicalInfo field
    if (state.patients && !state.patients.updateMedicalInfo) {
      state.patients.updateMedicalInfo = {
        loading: false,
        error: null,
        success: false,
      };
    }

    // Ensure auth persistence is consistent: if tokens are missing/empty, clear user as well
    if (state.auth) {
      const access = state.auth.tokens?.access;
      const refresh = state.auth.tokens?.refresh;
      const isEmpty = (v: any) => v === '' || v === null || v === undefined;
      if (!state.auth.tokens || isEmpty(access) || isEmpty(refresh)) {
        state.auth.user = null;
        state.auth.tokens = { access: null, refresh: null };
      }
    }
    return Promise.resolve(state);
  },
};

// Define the root state type
export interface RootState {
  treatmentPlan: ReturnType<typeof treatmentPlanReducer>;
  auth: ReturnType<typeof authReducer>;
  patients: ReturnType<typeof patientsReducer>;
  analytics: ReturnType<typeof analyticsReducer>;
  users: ReturnType<typeof usersReducer>;
}

// Create persisted reducers
const rootReducer = combineReducers({
  treatmentPlan: persistReducer(treatmentPlanPersistConfig, treatmentPlanReducer),
  auth: persistReducer(authPersistConfig, authReducer),
  patients: patientsReducer,
  analytics: analyticsReducer,
  users: usersReducer,
});

const persistedReducer = persistReducer(persistConfig, rootReducer);

// Create saga middleware
const sagaMiddleware = createSagaMiddleware();

// Configure redux-logger
const logger = createLogger({
  collapsed: false, // Don't collapse logs, make them fully visible
  duration: true,
  diff: true,
  // Log everything, including persist actions
  // predicate: (_, action) => !action.type.includes('persist')
});

// Configure store
export const store = configureStore({
  reducer: persistedReducer,
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({
      serializableCheck: {
        // Ignore non-serializable values for these actions
        ignoredActions: [
          'persist/PERSIST',
          'persist/REHYDRATE',
          'treatmentPlan/transcribeAudio', // <-- add this line
        ],
        ignoredActionPaths: ['payload'], // optional, to ignore all payloads
      },
    }).concat(sagaMiddleware, logger),
}) as EnhancedStore<RootState>; // Type assertion to resolve the naming issue

// Run sagas
sagaMiddleware.run(rootSaga);

// Create persistor
export const persistor = persistStore(store);

// Export store
export default store;

export type AppRootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
